<!-- eslint-disable vue/order-in-components -->
<template>
  <div class="bg-white">
    <Head>
      <Title>{{ $messages('features-debit-card-explore-discounts.meta.title') }}</Title>
      <Meta name="description" hid="description" :content="$messages('features-debit-card-explore-discounts.meta.description')" />
      <Meta name="og:image" hid="og:image" :content="ogImage" />
    </Head>

    <BlocksServiceHeroNew :hero="hero" />

    <div
      class="container flex flex-col items-center py-12 md:py-20"
      data-test-id="discount-list-component"
    >
      <h2 class="px-4 font-druk font-medium text-display-10 md:text-display-14 uppercase mb-8 text-center leading-none">
        {{ $messages('label.all_discounts') }}
      </h2>

      <div class="dropdown mb-4 md:mb-6">
        <span class="uppercase font-gt-america-extended font-light text-tiniest md:text-smallest leading-3 md:leading-4 text-middle">{{ $messages('label.categories') }}</span>
        <MajorityUiMajorityDropdown v-model="category" :items="getAllDiscountCategories" has-search @selected="updateDiscounts($event, 'category')" />
      </div>
      <div class="dropdown">
        <span class="uppercase font-gt-america-extended font-light text-tiniest md:text-smallest leading-3 md:leading-4 text-middle">{{ $messages('label.location') }}</span>
        <MajorityUiMajorityDropdown v-model="city" :items="getAllCities" has-search @selected="updateDiscounts($event, 'city')" />
      </div>
      <div class="discount-list-wrapper flex flex-col justify-center items-center mt-6 md:mt-12">
        <TransitionGroup
          name="list"
        >
          <BlocksDiscountListItem
            v-for="discount in discounts"
            :key="discount.id"
            :discount="discount"
            :is-loading="isFirstLoading"
            class="mb-6 last:mb-0 w-full"
          />
        </TransitionGroup>
        <span
          v-if="!hasDiscounts"
          class="uppercase w-full text-center font-gt-america-extended font-light text-smallest md:text-base leading-4 md:leading-5 -tracking-04-em animate-fade-in"
        >
          {{ $messages('features-debit-card-explore-discounts.label.no_discount') }}
        </span>
      </div>

      <div
        v-if="hasMoreToShow"
        class="animate-fade-in mt-8 md:mt-12"
      >
        <button
          class="btn btn-infra-violette flex justify-center items-center"
          type="button"
          :disabled="showMoreLoading"
          @click.prevent="showMore"
        >
          <span
            v-if="showMoreLoading"
            class="spinner text-white mr-2"
          />
          {{ $messages('label.show_more') }}
        </button>
      </div>
    </div>

    <BlocksUserReviewList />

    <BlocksFaqV2
      v-bind="$messages('faq')"
    />

    <BlocksMiniCheckout ref="checkout" />
  </div>
</template>

<script>
import { optimize } from '@/utils/image-optimization'
import PageMixing from '@/mixins/page-mixin'

export default {
  name: 'DebitCardExploreDiscounts',
  mixins: [PageMixing('features-debit-card-explore-discounts', 'Features - Debit Visa Card Explore Discounts', 'features-debit-card-explore-discounts.meta.title')],
  layout: 'default',
  async setup () {
      const communityClient = useCommunity()
      return {
        communityClient
      }
  },
  data () {
    return {
      hero: {
        fullScreenImage: false,
        backgroundColor: {
          hex: '#fff5f0'
        },
        image: {
          url: 'https://majority.imgix.net/web-assets/shared/phone-and-card.png',
          alt: 'Phone and card'
        },
        title: this.$messages('features-debit-card-explore-discounts.hero.title'),
        titleColor: {
          hex: '#000000'
        },
        bodyText: this.$messages('features-debit-card-explore-discounts.hero.body'),
        bodyTextColor: {
          hex: '#000000'
        },
        primaryCta: [
          {
            buttonType: 'none',
            outline: 'none',
            buttonColor: {
              hex: '#000000'
            },
            textColor: {
              hex: '#FFFFFF'
            },
            buttonText: this.$messages('features-majority-account-no-fees.hero.cta')
          }
        ]
      },
      cta: this.$messages('features-debit-card-explore-discounts.hero.cta'),
      breadcrumbs: [
        {
          title: this.$messages('label.home'),
          to: this.$mRouter.getLocalRoute({ name: 'index' })
        },
        {
          title: this.$messages('page_labels.debit_card'),
          to: this.$mRouter.getLocalRoute({ name: 'visa-debit-card' })
        },
        {
          title: this.$messages('features-debit-card-explore-discounts.hero.title')
        }
      ],

      category: { id: 'all' },
      city: { id: 'all' },
      discounts: [],
      page: 0,
      pageLength: 10,
      discountCategories: [],
      cities: [],
      isFirstLoading: true,
      showMoreLoading: false
    }
  },
  computed: {
    getAllDiscountCategories () {
      const categories = JSON.parse(JSON.stringify(this.discountCategories)).sort((a, b) => a.name.localeCompare(b.name))
      return [
        { id: 'all', name: this.$messages('label.all') },
        ...categories
      ]
    },
    getAllCities () {
      const categories = JSON.parse(JSON.stringify(this.cities)).sort((a, b) => a.name.localeCompare(b.name))
      return [
        { id: 'all', name: this.$messages('label.all') },
        ...categories
      ]
    },
    hasMoreToShow () {
      return this.communityClient.hasMoreDiscountsByCategoryCityIds(this.discounts.length, { categoryId: this.category.id, cityId: this.city.id })
    },
    hasDiscounts () {
      return this.discounts.length
    },
    take () {
      const firstPage = 3
      return this.page === 0 ? firstPage : ((this.page * this.pageLength) + firstPage)
    },
    ogImage () {
      return optimize(
        'https://majority.imgix.net/web-assets/meta/og.png',
        false,
        { w: 1200, h: 630, fit: 'fill', fill: 'solid', 'fill-color': '283A35' }
      )
    }
  },
  async created () {
    await this.load()
  },
  methods: {
    async load () {
      const locale = this.$i18n.locale
      const defaultTake = 3
      const params = { locale }

      this.discountCategories = await this.communityClient.getDiscountCategories(this.$i18n.locale)
      this.cities = await this.communityClient.getCities()

      if (this.$route.query.ca && this.discountCategories.find(x => x.id === this.$route.query.ca)) {
        params.categoryId = this.$route.query.ca
      }
      if (this.$route.query.ci && this.cities.find(x => x.id === this.$route.query.ci)) {
        params.cityId = this.$route.query.ci
      }

      const pg = ((parseInt(this.$route.query.p) || 0) < 0) ? 0 : parseInt(this.$route.query.p) || 0
      params.take = (pg * 10) + defaultTake
      this.category = { id: params.categoryId || 'all' }
      this.city = { id: params.cityId || 'all' }
      this.page = pg
      this.discounts = await this.communityClient.getDiscountsByCategoryCityIds( this.category.id, this.city.id, params.take, this.$i18n.locale)      
      this.isFirstLoading = false
    },
    onCtaHeroClick () {
      this.scrollToCheckout()
      this.$track.linkClicked('debitCardDiscountsHeroButton', 'cta', { linkText: 'scroll-to-checkout' })
    },
    scrollToCheckout () {
      this.$refs.checkout.$el.querySelector('#mini-checkout-form').scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    },
    async firstLoad (categoryId, cityId, page = 0) {
      try {
        this.isFirstLoading = true
        this.page = page
        this.discounts = await this.communityClient.getDiscountsByCategoryCityIds( categoryId, cityId, this.take, this.$i18n.locale)
      } finally {
        this.isFirstLoading = false
      }
    },
    async showMore () {
      try {
        this.showMoreLoading = true
        this.page += 1
        this.discounts = await this.communityClient.getDiscountsByCategoryCityIds( this.category.id, this.city.id, this.take, this.$i18n.locale)
      } finally {
        this.showMoreLoading = false
        this.$mRouter.replaceLocalRoute({ query: { ...this.$route.query, p: this.page } })
      }
    },
    async updateDiscounts (item, field) {
      if (field === 'category') {
        this.category.id = item.id
      }

      if (field === 'city') {
        this.city.id = item.id
      }

      await this.firstLoad(this.category.id, this.city.id)

      const query = Object.assign({}, this.$route.query)
      delete query.ca
      delete query.ci
      delete query.p

      if (item.id !== 'all') {
        query.ca = this.category.id
      }

      if (this.city.id !== 'all') {
        query.ci = this.city.id
      }

      const r = this.$mRouter.getLocalRoute({ name: 'visa-debit-card-discounts-explore-discounts', query })
      this.$router.replace(r)
    }
  }
}
</script>

  <style lang="postcss" scoped>
  .dropdown {
    @apply w-full;

    @screen md {
      max-width: 322px;
    }
  }

  .discount-list-wrapper {
    @apply w-full;

    @screen md {
      max-width: 540px;
    }
  }

  .animate-fade-in {
    opacity: 0;
  }

  .list-enter-active {
    transition: all 0.8s cubic-bezier(0.76, 0, 0.24, 1);
    max-height: 500px;
  }

  .list-leave-active {
    transition: all 0.5s cubic-bezier(0.76, 0, 0.24, 1);
    max-height: 500px;
  }
  .list-enter,
  .list-leave-to
  {
    opacity: 0;
    max-height: 0px;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    margin: 0 !important;
  }
  </style>
